import { useEffect, useState } from 'react';
import Widget from '../../../components/Dashboard/Widget';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import { KPIsWidgetEnum, LongTitles } from '../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSpinner } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/inputs/Button';
import { showException } from 'utils/exception-helper';
import { downloadBlob } from 'utils/file-utils';
import { formatDate } from 'utils/date-helpers';
import { useKPIGetCancellationsByBooker } from 'api/kpi/kpi';
import { useExportExportCancellationsHook } from 'api/export/export';
import { KPICancellationDataPoint, KPIGroupBy } from 'api/model';
import { maxKPIDateRangeMs } from 'constants/AppConstants';

type SortField =
  | 'groupName'
  | 'homeDeliveryTotal'
  | 'homeDeliveryCancelled'
  | 'homeDeliveryCancelledPercentage'
  | 'purchaseTotal'
  | 'purchaseCancelled'
  | 'purchaseCancelledPercentage';
type SortDirection = 'asc' | 'desc';

const SortIndicator = ({
  field,
  sortField,
  sortDirection,
}: {
  field: SortField;
  sortField: SortField;
  sortDirection: SortDirection;
}) => {
  if (sortField !== field) return null;
  return <span>{sortDirection === 'asc' ? ' \u25B2' : ' \u25BC'}</span>;
};

const CancellationsWidget = () => {
  const [groupBy, setGroupBy] = useState<KPIGroupBy>(KPIGroupBy.Booker);
  const [sortField, setSortField] = useState<SortField>(
    'homeDeliveryCancelledPercentage'
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [isDownloading, setIsDownloading] = useState(false);

  const {
    dashboardState: {
      daterange,
      selectedSupplierIDs,
      selectedCustomerNames,
      selectedFromCountyIDs,
      selectedToCountyIDs,
      excludeCompanyNameContains,
    },
  } = useDashboardControls();

  const getKPICancellationsData = useKPIGetCancellationsByBooker();

  const startDate = daterange[0];
  const endDate = daterange[1];
  const startDateTime = startDate ? startDate.getTime() : null;
  const endDateTime = endDate ? endDate.getTime() : null;

  const isDateRangeTooLong =
    startDate &&
    endDate &&
    endDate.getTime() - startDate.getTime() > maxKPIDateRangeMs;

  useEffect(() => {
    if (startDate && endDate && !isDateRangeTooLong) {
      getKPICancellationsData.mutate({
        data: {
          startDate,
          endDate,
          supplierIDs: selectedSupplierIDs,
          customerNames: selectedCustomerNames,
          fromCountyIDs: selectedFromCountyIDs,
          toCountyIDs: selectedToCountyIDs,
          kpiGroupBy: groupBy,
          excludeCompanyNameContains: excludeCompanyNameContains ?? undefined,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startDateTime,
    endDateTime,
    selectedSupplierIDs,
    selectedCustomerNames,
    selectedFromCountyIDs,
    selectedToCountyIDs,
    groupBy,
    excludeCompanyNameContains,
    isDateRangeTooLong,
  ]);

  const downloadExcel = useExportExportCancellationsHook();

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortData = (
    data: KPICancellationDataPoint[]
  ): KPICancellationDataPoint[] => {
    return [...data].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortField) {
        case 'groupName':
          aVal = a.groupName || '';
          bVal = b.groupName || '';
          break;
        case 'homeDeliveryTotal':
          aVal = a.homeDeliveryTotal;
          bVal = b.homeDeliveryTotal;
          break;
        case 'homeDeliveryCancelled':
          aVal = a.homeDeliveryCancelled;
          bVal = b.homeDeliveryCancelled;
          break;
        case 'homeDeliveryCancelledPercentage':
          aVal = a.homeDeliveryCancelledPercentage;
          bVal = b.homeDeliveryCancelledPercentage;
          break;
        case 'purchaseTotal':
          aVal = a.purchaseTotal;
          bVal = b.purchaseTotal;
          break;
        case 'purchaseCancelled':
          aVal = a.purchaseCancelled;
          bVal = b.purchaseCancelled;
          break;
        case 'purchaseCancelledPercentage':
          aVal = a.purchaseCancelledPercentage;
          bVal = b.purchaseCancelledPercentage;
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'string') {
        const comparison = aVal.localeCompare(bVal as string);
        return sortDirection === 'asc' ? comparison : -comparison;
      }
      return sortDirection === 'asc'
        ? aVal - (bVal as number)
        : (bVal as number) - aVal;
    });
  };

  const currentData = getKPICancellationsData.data?.dataPoints || [];
  const sortedData = sortData(currentData);

  const groupLabel = groupBy === KPIGroupBy.Company ? 'Företag' : 'Säljare';

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.Cancellations]}
      count={undefined}
      isLoading={getKPICancellationsData.isLoading}
      info="Visar statistik över avbokningar och ombokningar. Uppdelat på Hemleveranser och Inköp."
    >
      <p style={{ fontWeight: 'bold', textAlign: 'center' }}>
        OBS: Datumen utgår från ärendets stängd-datum.
      </p>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
        <Button
          disabled={
            isDownloading || (!isDateRangeTooLong && currentData.length === 0)
          }
          onClick={async () => {
            try {
              setIsDownloading(true);
              const result = await downloadExcel({
                startDate: daterange[0]!,
                endDate: daterange[1]!,
                supplierIDs: selectedSupplierIDs,
                customerNames: selectedCustomerNames,
                fromCountyIDs: selectedFromCountyIDs,
                toCountyIDs: selectedToCountyIDs,
                kpiGroupBy: groupBy,
                excludeCompanyNameContains:
                  excludeCompanyNameContains ?? undefined,
              });
              if (result) {
                downloadBlob(
                  result,
                  `Nyckeltal_Avbok_${formatDate(daterange[0]!)}_${formatDate(daterange[1]!)}.xlsx`
                );
              }
            } catch (error) {
              showException(error);
            } finally {
              setIsDownloading(false);
            }
          }}
        >
          <FontAwesomeIcon icon={faDownload} /> Ladda ner
          {isDownloading ? (
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              style={{ marginLeft: '5px' }}
            />
          ) : null}
        </Button>
      </div>

      <div style={{ display: 'flex', marginBottom: '15px' }}>
        <button
          type="button"
          onClick={() => setGroupBy(KPIGroupBy.Booker)}
          style={{
            padding: '8px 16px',
            cursor: 'pointer',
            backgroundColor:
              groupBy === KPIGroupBy.Booker ? '#007bff' : '#f0f0f0',
            color: groupBy === KPIGroupBy.Booker ? 'white' : 'black',
            border: '1px solid #ccc',
            borderRadius: '4px 0 0 4px',
          }}
        >
          Per säljare
        </button>
        <button
          type="button"
          onClick={() => setGroupBy(KPIGroupBy.Company)}
          style={{
            padding: '8px 16px',
            cursor: 'pointer',
            backgroundColor:
              groupBy === KPIGroupBy.Company ? '#007bff' : '#f0f0f0',
            color: groupBy === KPIGroupBy.Company ? 'white' : 'black',
            border: '1px solid #ccc',
            borderLeft: 'none',
            borderRadius: '0 4px 4px 0',
          }}
        >
          Per företag
        </button>
      </div>

      {isDateRangeTooLong ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p>
            Datumintervallet är för långt för att visa data. Använd
            nedladdningsknappen för att exportera data.
          </p>
        </div>
      ) : sortedData.length === 0 ? (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          {getKPICancellationsData.isLoading ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <p>Ingen data att visa för vald filtrering.</p>
          )}
        </div>
      ) : (
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead
              style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}
            >
              <tr>
                <th
                  onClick={() => handleSort('groupName')}
                  style={{
                    cursor: 'pointer',
                    padding: '8px',
                    textAlign: 'left',
                    borderBottom: '2px solid #ddd',
                  }}
                >
                  {groupLabel}
                  <SortIndicator
                    field="groupName"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
                <th
                  onClick={() => handleSort('homeDeliveryTotal')}
                  style={{
                    cursor: 'pointer',
                    padding: '8px',
                    textAlign: 'right',
                    borderBottom: '2px solid #ddd',
                  }}
                >
                  Hemlev totalt
                  <SortIndicator
                    field="homeDeliveryTotal"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
                <th
                  onClick={() => handleSort('homeDeliveryCancelled')}
                  style={{
                    cursor: 'pointer',
                    padding: '8px',
                    textAlign: 'right',
                    borderBottom: '2px solid #ddd',
                  }}
                >
                  Hemlev avbok
                  <SortIndicator
                    field="homeDeliveryCancelled"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
                <th
                  onClick={() => handleSort('homeDeliveryCancelledPercentage')}
                  style={{
                    cursor: 'pointer',
                    padding: '8px',
                    textAlign: 'right',
                    borderBottom: '2px solid #ddd',
                  }}
                >
                  Hemlev %
                  <SortIndicator
                    field="homeDeliveryCancelledPercentage"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
                <th
                  onClick={() => handleSort('purchaseTotal')}
                  style={{
                    cursor: 'pointer',
                    padding: '8px',
                    textAlign: 'right',
                    borderBottom: '2px solid #ddd',
                  }}
                >
                  Inköp totalt
                  <SortIndicator
                    field="purchaseTotal"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
                <th
                  onClick={() => handleSort('purchaseCancelled')}
                  style={{
                    cursor: 'pointer',
                    padding: '8px',
                    textAlign: 'right',
                    borderBottom: '2px solid #ddd',
                  }}
                >
                  Inköp avbok
                  <SortIndicator
                    field="purchaseCancelled"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
                <th
                  onClick={() => handleSort('purchaseCancelledPercentage')}
                  style={{
                    cursor: 'pointer',
                    padding: '8px',
                    textAlign: 'right',
                    borderBottom: '2px solid #ddd',
                  }}
                >
                  Inköp %
                  <SortIndicator
                    field="purchaseCancelledPercentage"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((dp, index) => (
                <tr
                  key={dp.groupName || index}
                  style={{ borderBottom: '1px solid #eee' }}
                >
                  <td style={{ padding: '8px' }}>
                    {dp.groupName || '(Ingen)'}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>
                    {dp.homeDeliveryTotal}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>
                    {dp.homeDeliveryCancelled}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>
                    {dp.homeDeliveryCancelledPercentage.toFixed(1)}%
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>
                    {dp.purchaseTotal}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>
                    {dp.purchaseCancelled}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>
                    {dp.purchaseCancelledPercentage.toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Widget>
  );
};

export default CancellationsWidget;
